import React from "react";

import { useTranslation } from "react-i18next";

import { ActivateWalletButton } from "@components-shared/ActivateWalletButton";
import { ChangeShopButton } from "@components-shared/ChangeShopButton";
import { RefreshBalanceBtn } from "@components/buttons";
import { WrappedNavLink } from "@components/navigation";
import { ChatEntryPoint } from "@data-types/chat";
import { useRootAppStore } from "@hooks";
import { useAppContext } from "@state/hooks/shared";
import { isGamesTheme } from "@utils";
import classNames from "classnames";
import { observer } from "mobx-react";

import { HeaderLoginHistory, HeaderUserBalanceDisplay } from ".";

export default observer(function HeaderUserMenu(props: {
	isUserMenuOpen: boolean;
	setIsUserMenuOpen: (value: boolean) => void;
}) {
	const { isUserMenuOpen, setIsUserMenuOpen } = props;

	const { t } = useTranslation();

	const { culture } = useAppContext();

	const rootStore = useRootAppStore();

	const { unreadSupportMessages } = rootStore.chatViewStore;
	const user = rootStore.userAuthStore;

	if (!isUserMenuOpen) {
		return null;
	}

	return (
		<div className="dropdown--user">
			<div className="dropdown--user__head">
				<div className="dropdown--user__name">
					{user.user?.username}
				</div>

				<RefreshBalanceBtn
					type="button"
					className="dropdown--user__refresh btn--square btn--square--sml btn--primary--inverted btn--animate-rotate"
					btnContent={
						<i className="u-icon u-icon--sml u-icon--refresh" />
					}
					title="Reload balance"
				/>
			</div>

			<div className="dropdown--user__accounts">
				<ul className="usermenu__wallets">
					<HeaderUserBalanceDisplay isInMenu={true} />
				</ul>
			</div>
			<div className="u-mar--bottom--xsml">
				<ChangeShopButton setIsNavMenuOpen={setIsUserMenuOpen} />
			</div>
			<div className="u-mar--bottom--xsml">
				<ActivateWalletButton setIsNavMenuOpen={setIsUserMenuOpen} />
			</div>
			<div className="dropdown--user__history">
				<HeaderLoginHistory />
			</div>
			<div className="dropdown--user__menu">
				{rootStore.requiredUserActionStore.userActionStatus && (
					<div className="dropdown--user__required">
						<button
							type="button"
							onClick={() => {
								rootStore.requiredUserActionStore.showPopup();
								setIsUserMenuOpen(false);
							}}
							className={classNames(
								"btn btn--med btn--block btn--icon",
								{
									"btn--danger":
										rootStore.userAuthStore
											.isUserCritical ||
										(rootStore.userAuthStore
											.isUserCritical &&
											rootStore.userAuthStore
												.isUserWarning),
									"btn--warning":
										rootStore.userAuthStore.isUserWarning &&
										!rootStore.userAuthStore.isUserCritical,
									"btn--primary--inverted":
										!rootStore.userAuthStore
											.isUserCritical &&
										!rootStore.userAuthStore.isUserWarning,
								}
							)}
						>
							<i
								className={classNames("u-icon u-icon--base", {
									"u-icon--alert-triangle u-color--core--snow":
										rootStore.userAuthStore
											.isUserCritical ||
										(rootStore.userAuthStore
											.isUserCritical &&
											rootStore.userAuthStore
												.isUserWarning),
									"u-icon--alert-circle u-color--core--nero":
										rootStore.userAuthStore.isUserWarning &&
										!rootStore.userAuthStore.isUserCritical,
									"u-icon--info-circle u-color--sent--warning":
										!rootStore.userAuthStore
											.isUserCritical &&
										!rootStore.userAuthStore.isUserWarning,
								})}
							/>
							<span className="btn__label">
								{t("WELCOME_POPUP.REQUIRED_ACTIONS_TITLE")}
							</span>
						</button>
					</div>
				)}

				<div className="dropdown--user__title">
					{t<string>("HEADER.MY_ACCOUNT")}
				</div>

				<ul className="dropdown--user__list">
					{!isGamesTheme() && (
						<li
							className="dropdown--user__item"
							onClick={() => setIsUserMenuOpen(false)}
						>
							<WrappedNavLink
								to={`/${culture}/my-bets`}
								className="dropdown--user__link"
							>
								<span className="dropdown--user__label">
									{t("HEADER.MY_BETS")}
								</span>
								<i className="u-icon u-icon--tny u-icon--caret--right" />
							</WrappedNavLink>
						</li>
					)}

					<li
						className="dropdown--user__item"
						onClick={() => setIsUserMenuOpen(false)}
					>
						<WrappedNavLink
							to={`/${culture}/account-statement`}
							className="dropdown--user__link"
						>
							<span className="dropdown--user__label">
								{t("HEADER.ACCOUNT_STATEMENT")}
							</span>
							<i className="u-icon u-icon--tny u-icon--caret--right" />
						</WrappedNavLink>
					</li>

					{user.isInternetUser ? (
						<>
							<li
								className="dropdown--user__item"
								onClick={() => setIsUserMenuOpen(false)}
							>
								<WrappedNavLink
									to={`/${culture}/deposit`}
									className="dropdown--user__link"
								>
									<span className="dropdown--user__label">
										{t("DEPOSIT.TITLE")}
									</span>
									<i className="u-icon u-icon--tny u-icon--caret--right" />
								</WrappedNavLink>
							</li>
							<li
								className="dropdown--user__item"
								onClick={() => setIsUserMenuOpen(false)}
							>
								<WrappedNavLink
									to={`/${culture}/withdrawal`}
									className="dropdown--user__link"
								>
									<span className="dropdown--user__label">
										{t("WITHDRAWAL.TITLE")}
									</span>
									<i className="u-icon u-icon--tny u-icon--caret--right" />
								</WrappedNavLink>
							</li>
						</>
					) : null}

					{(WEBPACK_IS_CASINO_ENABLED ||
						WEBPACK_IS_CASINO_LIVE_ENABLED ||
						isGamesTheme()) && (
						<li
							className="dropdown--user__item"
							onClick={() => setIsUserMenuOpen(false)}
						>
							<WrappedNavLink
								to={`/${culture}/gaming-history`}
								className="dropdown--user__link"
							>
								<span className="dropdown--user__label">
									{t("FOOTER.GH_HEAD")}
								</span>
								<i className="u-icon u-icon--tny u-icon--caret--right" />
							</WrappedNavLink>
						</li>
					)}
					<li
						className="dropdown--user__item"
						onClick={() => setIsUserMenuOpen(false)}
					>
						<WrappedNavLink
							to={`/${culture}/account-settings/personal-data`}
							className="dropdown--user__link"
						>
							<span className="dropdown--user__label">
								{t("HEADER.ACCOUNT_SETTINGS")}
							</span>
							<i className="u-icon u-icon--tny u-icon--caret--right" />
						</WrappedNavLink>
					</li>

					<ChatNavigation setIsUserMenuOpen={setIsUserMenuOpen} />

					{WEBPACK_IS_SUPPORT_CHAT_AVAILABLE && (
						<li
							className="dropdown--user__item"
							onClick={() => setIsUserMenuOpen(false)}
						>
							<WrappedNavLink
								to={`/${culture}/support-chat`}
								className="dropdown--user__link u-display--f--align-center"
							>
								<span className="dropdown--user__label">
									<i className="u-icon u-icon--base u-icon--support--alt u-mar--right--xtny">
										{unreadSupportMessages && (
											<div className="tag tag--dot" />
										)}
									</i>
									<span className="u-align--v--middle u-mar--right--xtny">
										{t("CHAT.SUPPORT.TITLE")}
									</span>
								</span>

								<i className="u-icon u-icon--tny u-icon--caret--right" />
							</WrappedNavLink>
						</li>
					)}
				</ul>
			</div>
			<div className="dropdown--user__footer">
				<button
					disabled={user.isLogoutDisabled}
					className="btn btn--base btn--block btn--primary--inverted btn--icon"
					onClick={async () => {
						await rootStore.logoutPopupStore.displayLogoutOption();
						setIsUserMenuOpen(false);
					}}
				>
					<i className="u-icon u-icon--sml u-icon--logout" />
					<span>{t("HEADER.LOGOUT")}</span>
				</button>
			</div>
		</div>
	);
});

const ChatNavigation = observer(function ChatNavigation(props: {
	setIsUserMenuOpen: Function;
}) {
	const { t } = useTranslation();
	const { chatViewStore } = useRootAppStore();

	if (!WEBPACK_IS_CHAT_AVAILABLE) {
		return null;
	}

	return (
		<li
			className="dropdown--user__item"
			onClick={() => {
				props.setIsUserMenuOpen(false);
				chatViewStore.onInitialize(ChatEntryPoint.USER_MENU, null);
			}}
		>
			<span className="dropdown--user__link">
				<span className="dropdown--user__label">{t("CHAT.TITLE")}</span>
				{chatViewStore.isDisposing ? (
					<span className="btn--load btn--load--primary u-mar--auto"></span>
				) : (
					<i className="u-icon u-icon--tny u-icon--caret--right" />
				)}
			</span>
		</li>
	);
});
